import { IncomingMessage, ServerResponse } from 'http';
import Cookies from 'js-cookie';

/*
  This code is not used at the moment, however the scaffolding could be used in other features.
  The goal of this file is to provide support for server-side cookies. Cookies are set on the domain name of the server.
  That means those cookies are also accessible in the API.

  Here is how to use it:
  - to set the cookie in a page:
    export const getServerSideProps: GetServerSideProps = async context => {
      // Important: This is boilerplate code and should be reused in all pages
      const cookieCrumbs = getUTMQueryParams(context.req);
      setCookieInResponse(context.res, cookieCrumbs);

      return {
        props: {},
      };
    }
  - to get the cookie values in the API:
    const cookieCrumbs = getCookieCrumbsI(req);
    const utmCampaign = cookieCrumbs['utm_campaign'];
    const utmMedium = cookieCrumbs['utm_medium'];
    const utmSource = cookieCrumbs['utm_source'];
    const utmTerm = cookieCrumbs['utm_term'];
*/

type ExtractQueryParamsFromRequestType = (request: IncomingMessage, paramNames: Array<string>) => Record<string, string>;
export const extractQueryParamsFromRequest: ExtractQueryParamsFromRequestType = (request, paramNames) => {
  const values: Record<string, string> = {};
  if (request.headers.host && request.url) {
    const url = new URL(request.url, `http://${request.headers.host}`);
    paramNames.forEach((paramName) => {
      if (url.searchParams.has(paramName)) {
        values[paramName] = url.searchParams.get(paramName) as string;
      }
    });
  }
  return values;
};

export const setCookieInResponse = (response: ServerResponse, cookieCrumbs: Record<string, string>): void => {
  const cookieKeys = Object.keys(cookieCrumbs);
  if (cookieKeys.length > 0) {
    response.setHeader(
      'Set-Cookie',
      cookieKeys.map((cookieKey) => `${cookieKey}=${cookieCrumbs[cookieKey]}`),
    );
  }
};

export const createGetUTMQueryParams =
  (extractQueryParamsFromRequestI: ExtractQueryParamsFromRequestType) =>
  (request: IncomingMessage): Record<string, string> =>
    extractQueryParamsFromRequestI(request, ['utm_campaign', 'utm_medium', 'utm_source', 'utm_term']);
export const getUTMQueryParams = createGetUTMQueryParams(extractQueryParamsFromRequest);

export type GetCookieCrumbsType = (request: IncomingMessage) => Record<string, string>;
export const getCookieCrumbs: GetCookieCrumbsType = (request) => {
  const cookieCrumbs: Record<string, string> = {};
  if (request.headers.cookie) {
    const nameValuePairs = request.headers.cookie.split('; ');
    nameValuePairs.forEach((nameValue) => {
      const [name, value] = nameValue.split('=');
      cookieCrumbs[name] = value;
    });
  }
  return cookieCrumbs;
};

/**
 * Helper function for setting a cookie on the saucelabs.com domain
 */
export const setCookiesWithDomain = (key: string, value: string, expires?: number | Date) => {
  const { hostname } = window.location;

  let domain = '.saucelabs.com';
  if (hostname.includes('localhost')) domain = 'localhost';
  else if (hostname.includes('staging.')) domain = '.saucelabs.net';

  Cookies.set(key, value, { domain, path: '/', expires });
};

export const getCookie = (key: string) => Cookies.get(key);

/**
 * Helper function that sets a mixpanel device ID in a cookie
 * so that sesisons can be linked across subdomains
 */
export const setMixpanelDeviceId = (mixpanelDistinctId: string | undefined | null = null) => {
  const deviceId = getCookie('mixpanel_device_id');

  // alias the mixpanel id with ajs_anonymous_id
  const ajsAnonymousId = getCookie('ajs_anonymous_id');
  const isMixpanelAliased = getCookie('is_mixpanel_aliased');
  if (ajsAnonymousId && mixpanelDistinctId && !isMixpanelAliased) {
    (window as any)?.mixpanel?.alias(mixpanelDistinctId, ajsAnonymousId);
    setCookiesWithDomain('is_mixpanel_aliased', 'true', 2592000);
  }

  if (deviceId && mixpanelDistinctId && deviceId !== mixpanelDistinctId) {
    window?.analytics?.alias(mixpanelDistinctId, deviceId);
    return true;
  }

  if (mixpanelDistinctId) {
    // save mixpanel distinct ID to a session cookie that works across subdomains
    setCookiesWithDomain('mixpanel_device_id', mixpanelDistinctId, 2592000);
    return true;
  }

  return false;
};

export const setRedirectCookie = (redirectUrl: string) => {
  // set redirect_to cookie to be used by app.saucelabs.com
  if (typeof window !== 'undefined') {
    const { host } = window.location;
    const isStaging = host.includes('staging');
    const domain = isStaging ? '.saucelabs.net' : '.saucelabs.com';
    if (redirectUrl) {
      Cookies.set('redirect_to', redirectUrl, { domain });
    } else {
      Cookies.remove('redirect_to');
    }
  }
};
